import { useState } from "react";
import { getSubmissions } from "@/api";
import { useAuth } from "@/auth";
import { useTenants } from "@/hooks/useTenants";
import { useResource } from "@/hooks/useResource";
import { TenantSelect } from "@/components/TenantSelect";
import { SubmissionsTable } from "@/components/SubmissionsTable";

export function Submissions() {
  const { status } = useAuth();
  const { tenants, tenant, setTenant, error: tenantsError } = useTenants();
  // Backend paginates at page_size=50; without this the header advertises a
  // total the table can never reach. `page` is 1-based to match the API.
  const [page, setPage] = useState(1);
  // The api client reads its own creds; useResource never gates on creds — both
  // auth lanes (Basic and the Google cookie, status === "cookie") fetch alike.
  const ready = Boolean(tenant);
  const { data, error: dataError } = useResource(
    () => getSubmissions({ tenant, page }),
    [status, tenant, page],
    { enabled: ready },
  );
  const error = tenantsError ?? (dataError && "Could not load submissions.");

  // Switching client resets to page 1 in the SAME state change as the tenant,
  // so deps change once (no separate effect double-fetch).
  const onTenantChange = (key: string) => {
    setTenant(key);
    setPage(1);
  };

  // Use the server-confirmed page/total so the controls reflect what loaded.
  const totalPages = data && data.page_size > 0 ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
  const currentPage = data?.page ?? page;

  return (
    <div className="space-y-8">
      <div className="rise">
        <h1 className="font-display text-3xl font-bold uppercase tracking-tight">Leads</h1>
        <p className="mt-1 text-sm text-dim">Captured quiz submissions.</p>
      </div>

      <div className="rise card flex flex-wrap items-end gap-5 p-4">
        <TenantSelect id="sub-tenant" tenants={tenants} value={tenant} onChange={onTenantChange} />
        {data && (
          <p className="ml-auto self-center font-display text-sm uppercase tracking-[0.16em] text-dim">
            {data.total} total
          </p>
        )}
      </div>

      {error && <p role="alert" className="text-coral">{error}</p>}
      {ready && data ? (
        <>
          <div className="card overflow-hidden">
            <SubmissionsTable rows={data.submissions} />
          </div>
          <div className="flex items-center justify-between gap-4">
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
            >
              Prev
            </button>
            <span className="font-display text-xs uppercase tracking-[0.16em] text-dim">
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setPage((p) => p + 1)}
              disabled={currentPage >= totalPages}
            >
              Next
            </button>
          </div>
        </>
      ) : (
        !error && <p className="text-dim">Loading…</p>
      )}
    </div>
  );
}
